import {
  AddRounded,
  CorporateFareRounded,
  EditRounded,
  SearchRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import MainLayout from "../../layouts/main/main_layout";
import Loading from "../../components/loading/loading";
import useUser from "../../providers/user/user_provider";
import { getOrganizations } from "../../services/organizations/organizations";
import { organizations_add, organizations_edit } from "../../routes/routes_path";
import { Organization, OrganizationStatus } from "./model/organization";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../users/components/table_toolkit/table_toolkit";
import {
  black,
  green,
  greenLight,
  greyEnabled,
  orange,
  orangeLightActive,
  purple,
  purpleLight,
  red,
  redLight,
} from "../../theme/colors";

const ROWS_PER_PAGE = 8;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

const STATUS_STYLES: Record<OrganizationStatus, { bg: string; color: string }> = {
  active: { bg: greenLight, color: green },
  suspended: { bg: orangeLightActive, color: orange },
  archived: { bg: redLight, color: red },
};

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function Organizations() {
  const navigate = useNavigate();
  const { userPermissions } = useUser();

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue]);

  const { data: organizationsData, isLoading, isError } = useQuery(
    ["organizations", page, debouncedSearchValue],
    async () => {
      let response = await getOrganizations({
        page,
        size: ROWS_PER_PAGE,
        keyword: debouncedSearchValue,
      });
      return response.data.data as { total: number; list: Organization[] };
    }
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const count = organizationsData?.total || 0;
  const list = organizationsData?.list || [];

  const startRange = (page - 1) * ROWS_PER_PAGE + 1;
  const endRange = Math.min(page * ROWS_PER_PAGE, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  return (
    <MainLayout>
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: purpleLight,
            flexShrink: 0,
          }}
        >
          <CorporateFareRounded sx={{ color: purple, fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color={black}>
            Organisations
          </Typography>
          <Typography variant="body2" color={greyEnabled}>
            Manage tenant organisations and their module subscriptions.
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ p: { xs: 2.5, sm: 3 }, border: "1px solid #ECECEC", borderRadius: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search by name or org code"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {userPermissions?.isPlatformOrg && (
            <Button
              onClick={() => navigate(organizations_add)}
              variant="contained"
              disableElevation
              startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Add Organisation
            </Button>
          )}
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            gap={0.5}
          >
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the organisations list. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message="No organisations found"
            hint="Try adjusting your search, or add a new organisation."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[{ label: "Organisation" }, { label: "Actions" }]}
              lastColumnWidth={80}
              align="left"
            />
            {list.map((item) => (
              <TableRow key={item.id}>
                <TableCell label="Organisation">
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography fontWeight={600} color={black} noWrap>
                      {item.name}
                    </Typography>
                    <Chip
                      label={capitalize(item.status)}
                      size="small"
                      sx={{
                        bgcolor: STATUS_STYLES[item.status]?.bg,
                        color: STATUS_STYLES[item.status]?.color,
                        fontWeight: 600,
                        height: 20,
                      }}
                    />
                    {item.isPlatform && (
                      <Chip
                        label="Platform"
                        size="small"
                        sx={{ bgcolor: purpleLight, color: purple, fontWeight: 600, height: 20 }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color={greyEnabled} noWrap>
                    {item.orgCode}
                    {item.tier ? ` · ${item.tier}` : ""}
                    {` · ${item.activeModuleCount} active module${item.activeModuleCount === 1 ? "" : "s"}`}
                  </Typography>
                </TableCell>
                <TableCell isLast lastColumnWidth={80}>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                    {userPermissions?.isPlatformOrg && (
                      <Tooltip title="Edit organisation">
                        <IconButton
                          onClick={() => navigate(`${organizations_edit}/${item.id}`)}
                          sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                        >
                          <EditRounded sx={{ color: purple, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {showList && (
        <CardActions sx={{ px: 4, pb: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={ROWS_PER_PAGE}
            />
          </Box>
        </CardActions>
      )}
    </MainLayout>
  );
}
